<template>
  <v-container>
    <v-snackbar v-model="snackbar" class="elevation-24" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn icon="mdi-close" @click="snackbar = false"> </v-btn>
      </template>
    </v-snackbar>

    <v-card class="pa-5 mb-4" v-if="event" variant="outlinted" elevation="2">
      <!-- Event Image with Title Overlay -->
      <v-img
        :src="'https://sunrise-event.com/uploads/' + event.image"
        height="300px"
        width="100%"
        class="white--text align-end mb-4"
        gradient="to bottom, rgba(0,0,0,.3), rgba(0,0,0,.7)"
        v-if="event.image"
      >
        <v-card-title class="headline">{{ event.name }}</v-card-title>
      </v-img>
      <v-card-title v-else>{{ event.name }}</v-card-title>

      <v-card-text>
        <v-row>
          <!-- Event Details -->
          <v-col cols="12" sm="6">
            <p><strong>Description:</strong> {{ event.description }}</p>
            <p><strong>Date:</strong> {{ event.date }}</p>
            <p v-if="event.location">
              <strong>Location:</strong> {{ event.location }}
            </p>
          </v-col>
          <v-col cols="12" sm="6">
            <p v-if="event.organizer">
              <strong>Organizer:</strong> {{ event.organizer }}
            </p>
            <p v-if="event.category">
              <strong>Category:</strong> {{ event.category }}
            </p>
            <p v-if="event.website">
              <strong>Website:</strong>
              <a :href="event.website" target="_blank" rel="noopener">{{
                event.website
              }}</a>
            </p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="pa-4 my-4" variant="outlinted" elevation="2">
      <v-data-table
        :headers="headers"
        :items="attendees"
        item-key="id"
        :items-per-page="5"
        :search="search"
      >
        <template v-slot:[`item.checked_in`]="{ item }">
          <v-checkbox
            v-model="item.checked_in"
            :true-value="1"
            :false-value="0"
            readonly
            color="success"
          ></v-checkbox>
        </template>
      </v-data-table>
    </v-card>
    <v-spacer></v-spacer>

    <!-- Attendees List -->
    <v-btn
      prepend-icon="mdi-export"
      block
      color="success"
      @click="exportCSV(event.id)"
      >Export Attendees</v-btn
    >
    <v-spacer class="my-3"></v-spacer>

    <!-- Attendees List -->
    <v-btn
      prepend-icon="mdi-download"
      block
      color="info"
      @click="sendInvitations(event.id)"
      >Download Invitations</v-btn
    >
    <v-spacer></v-spacer>

    <v-row class="my-3">
      <v-col cols="6">
        <v-file-input
          v-model="uploadedFile"
          label="Upload Excel File"
          accept=".xlsx, .xls"
          prepend-icon="mdi-upload"
        ></v-file-input>
      </v-col>
      <v-col cols="6">
        <v-btn
          color="success"
          class="my-2"
          @click="uploadAttendees"
          :disabled="!uploadedFile"
          block
        >
          Upload Attendees
        </v-btn>
      </v-col>
      <v-col cols="12">
        <p>{{ message }}</p>
      </v-col>
    </v-row>

    <v-spacer></v-spacer>
    <!-- Back Button -->
    <v-btn
      prepend-icon="mdi-back"
      @click="goBack"
      color="grey-darken-3"
      class="my-4"
      text
      block
    >
      Back to Events
    </v-btn>
  </v-container>
</template>

<script>
import axios from "axios";
const XLSX = require("xlsx"); // For Node.js
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";

export default {
  props: ["id"], // Event ID from route
  data() {
    return {
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      event: null,
      uploadedFile: null,
      attendees: [],
      message: "",
      headers: [
        { title: "Name", value: "name", sortable: true },
        { title: "Email", value: "email" },
        { title: "Gender", value: "gender" },
        { title: "Age", value: "age" },
        { title: "Checked in ", value: "checked_in" },
      ],
    };
  },
  mounted() {
    var eventId = this.id;
    this.fetchEvent();
    this.fetchAttendees(eventId);
  },
  methods: {
    async fetchEvent() {
      try {
        const response = await axios.get(`/api/events/${this.id}`);
        this.event = response.data;
      } catch (error) {
        console.error("Error fetching event details:", error);
      }
    },
    async sendInvitations() {
      const zip = new JSZip();

      for (const invite of this.attendees) {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        const qrBoxWidth = 70;
        const qrBoxHeight = 80;
        const qrBoxX = (pageWidth - qrBoxWidth) / 2;
        const qrBoxY = 120;
        const qrSize = 50;
        const qrX = qrBoxX + (qrBoxWidth - qrSize) / 2;
        const qrY = qrBoxY + 10;

        const qrCodeDataUrl = await QRCode.toDataURL(
          `${invite.event_id}*${invite.name}*${invite.email}`
        );

        doc.saveGraphicsState();
        doc.setTextColor(230, 230, 230);
        doc.setFontSize(50);
        doc.setFont("helvetica", "bold");
        doc.text("Sunrise Events", pageWidth / 2, pageHeight / 2, {
          align: "center",
          angle: 45,
        });
        doc.restoreGraphicsState();

        doc.setDrawColor("#000000");
        doc.setLineDashPattern([4, 2], 0);
        doc.setLineWidth(0.8);
        doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
        doc.setLineDashPattern([], 0);

        doc.setFillColor("#000000");
        doc.rect(10, 10, pageWidth - 20, 30, "F");
        doc.setTextColor("#FFFFFF");
        doc.setFontSize(18);

        const imageUrl = `https://sunrise-event.com/uploads/${invite.event_image}`;
        const imageBlob = await fetch(imageUrl).then((res) => res.blob());
        const imageReader = await this.blobToBase64(imageBlob);

        doc.addImage(imageReader, "PNG", 15, 12, qrSize / 2, qrSize / 2);
        doc.text(`${invite.event_name} — You're Invited!`, pageWidth / 2, 28, {
          align: "center",
        });

        doc.setFontSize(14);
        doc.setTextColor("#000000");
        doc.text(`Name: ${invite.name}`, 20, 55);
        doc.text(`Email: ${invite.email}`, 20, 65);
        doc.text(`Event ID: ${invite.event_id}`, 20, 75);

        doc.setFontSize(12);
        doc.setTextColor("#333333");
        doc.text(invite.description, 20, 90, { maxWidth: pageWidth - 40 });

        doc.setDrawColor("#888888");
        doc.setLineWidth(0.5);
        doc.rect(qrBoxX, qrBoxY, qrBoxWidth, qrBoxHeight);

        doc.addImage(qrCodeDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

        doc.setFontSize(11);
        doc.setTextColor("#333333");
        doc.text(
          "Present this code at the entrance for fast check-in.",
          pageWidth / 2,
          qrBoxY + qrBoxHeight + 30,
          { align: "center" }
        );

        doc.setFillColor("#000000");
        doc.rect(10, pageHeight - 30, pageWidth - 20, 20, "F");
        doc.setTextColor("#FFFFFF");
        doc.setFontSize(10);
        doc.text("Sunrise Events · © 2025", pageWidth / 2, pageHeight - 17, {
          align: "center",
        });

        // Add PDF to ZIP
        const pdfBlob = doc.output("blob");
        zip.file(`${invite.event_name}-${invite.name}.pdf`, pdfBlob);
      }

      // Generate and download ZIP
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "all_invitations.zip");
    },

    // Helper to convert blob to base64
    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },

    goBack() {
      this.$router.push("/");
    },
    exportArrayToExcel(data, filename = "data.xlsx") {
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.error("No data to export.");
        return;
      }

      // 1. Convert JSON array to worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);

      // 2. Create a new workbook and append the sheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

      // 3. Write to file (Node.js)
      XLSX.writeFile(workbook, filename);
    },
    fetchAttendees(eventId) {
      console.log("Fetching attendees for event ID:", eventId);
      console.log(
        "Fetching attendees from URL:",
        `/api/events/${eventId}/attendees/export`
      );

      axios.get(`/api/events/${eventId}/attendees/export`).then((response) => {
        console.log("Attendees:", response);
        this.attendees = response.data.data;
      });
    },
    exportCSV(eventId) {
      const attendees = this.attendees.map((att) => {
        return {
          name: att.name,
          email: att.email,
          gender: att.gender,
          age: att.age,
          checked_in: att.checked_in ? "Yes" : "No",
        };
      });
      this.exportArrayToExcel(attendees, `attendees_event_${eventId}.xlsx`);
    },
    async uploadAttendees() {
      try {
        const data = await this.uploadedFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const attendees = XLSX.utils.sheet_to_json(worksheet);

        console.log("attendees uploaded", attendees);

        // Validate structure
        if (!attendees.every((a) => a.name && a.email)) {
          this.message = 'Each row must have "name" and "email" fields.';
          this.snackbarText = 'Each row must have "name" and "email" fields.';
          this.snackbar = true;
          this.snackbarColor = "red";
          return;
        }

        const response = await axios
          .post(`/api/events/attendees/upload/${this.id}`, { attendees })
          .then((resp) => {
            this.snackbarText = resp != null ? resp.data.message : resp.data;
            this.snackbar = true;
            this.snackbarColor = "success";
          })
          .catch((err) => {
            this.snackbarText = err;
            this.snackbar = true;
            this.snackbarColor = "red";
          });

        const result = response;
        this.message = result || "Upload successful!";
        this.uploadedFile = null;
        this.fetchAttendees(this.id);
      } catch (error) {
        console.error(error);
        this.message = "Upload failed!" + error;
      }
    },
  },
};
</script>

<style scoped>
a {
  color: #1976d2;
  text-decoration: none;
}
a:hover {
  text-decoration: underline;
}
</style>
